/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { EntityEventType } from "./../contracts/entityEvent";
import Activity from "./../entities/activity";
import ActivityWindow from "./../entities/activityWindow";

import ActivityManager from "./../core/activityManager";
import { Logger } from "./../helpers/logger";
import * as util from "./../helpers/util";

import { WindowDefinition } from "./../entities/windowDefinition";
import { CloneOptions } from "../entities/cloneOptions";
import { Callback } from "../contracts/contracts";
import { AttachedActivityDescriptor } from "../entities/attachedActivityDescriptor";
import { Glue42 } from "../../../glue";

export interface My {

  activity: Activity;

  window: ActivityWindow;

  context: object;

  updateContext(context: object, callback?: any): Promise<object>;

  setContext(context: object, callback?: any): Promise<object>;

  onActivityJoined(callback: (activity: Activity) => void): void;

  onActivityLeft(callback: (activity: Activity) => void): void;

  onContextChanged(callback: (context: object, delta: object, removed: string[], activity: Activity) => void): void;

  clone(options: CloneOptions, callback: Callback<Activity>): Promise<Activity>;

  attach(activity: Activity | string, tag?: object): Promise<AttachedActivityDescriptor>;

  setFrameColor(color: string, callback: () => void): Promise<Activity>;

  getFrameColor(): string;

  onFrameColorChanged(callback: () => void): void;
}

/**
 * A lightweight, single activity oriented subset of the API which should be used by most activity applications.
 * Provides access to the activity of your application (my.activity) and shortcuts to the most commonly used methods
 * (e.g. my.createWindow is a shortcut for my.activity.createWindow)
 *
 * @module my
 */
export default class ActivityMy implements My {

  public _myAttached: ((state: AttachedActivityDescriptor) => void)[] = [];
  public _myDetached: ((state: AttachedActivityDescriptor) => void)[] = [];
  public _myAttachedTo: ((activity: Activity, state: AttachedActivityDescriptor) => void)[] = [];
  public _myDetachedFrom: ((detached: Activity, existing: Activity, state: AttachedActivityDescriptor) => void)[] = [];
  public _myActivityFrameColorChanged: ((frameColor: string) => void)[] = [];
  public _myActivityJoinedCallbacks: ((activity: Activity, event: any) => void)[] = [];
  public _myActivityRemovedCallbacks: ((activity: Activity, event: any) => void)[] = [];
  public _myContextUpdateCallbacks: ((context: object, delta: object, removed: string[], activity: Activity) => void)[] = [];
  public _w: ActivityWindow;
  public _m: ActivityManager;
  public _logger: Logger = Logger.Get(this);

  constructor(manager: ActivityManager, windows: any) {
    this._m = manager;
    manager.ready()
      .then((am) => {
        // Subscribe for events for driving "my" logic
        am.subscribeActivityContextChanged(this._subscribeMyContextChanged.bind(this));
        am.subscribeWindowEvents(this._subscribeMyWindowEvent.bind(this));
        am.subscribeActivitiesAttached(this._subscribeActivitiesAttached.bind(this));
        am.subscribeActivitiesDetached(this._subscribeActivitiesDetached.bind(this));
        if (windows) {
          windows.onWindowFrameColorChanged(this._subscribeWindowFrameColorChanged.bind(this));
        }
      });
  }

  /**
   * The current activity window; can be undefined (if the window is not part of any activity)
   *
   * @var {Window} window
   */
  public get window(): ActivityWindow {
    if (util.isUndefinedOrNull(this._w)) {
      const announcedWindows = this._m.announcedWindows;
      if (announcedWindows.length > 0) {
        this._w = announcedWindows[0];
      }
    }
    return this._w;
  }

  /**
   * The current activity that the application participates in.
   * Can be undefined (if currently not part of any activity)
   *
   * @var {module:activity} activity
   */
  public get activity(): Activity {
    const myWin = this.window;
    if (util.isUndefinedOrNull(myWin)) {
      return undefined;
    }
    return myWin.activity;
  }

  /**
   * Creates a new window and joins it to the activity
   *
   * @function createWindow
   * @param {string| windowDefinition} windowType The type of the window to be created
   * @returns {Promise<module:activityWindow>}
   */
  public createWindow(windowType: string | WindowDefinition): Promise<ActivityWindow> {
    return this._m.createWindow(this.activity, windowType);
  }

  /**
   * Creates a stacked set of windows and joins them to the
   *
   * @function createStackedWindows
   * @param {string[] | windowDefinition[]} windowTypes The types of the windows to be created
   * @param {number} timeout
   * @returns {Promise<module:activityWindow[]>}
   */
  public createStackedWindows(windowTypes: (string | WindowDefinition)[], timeout?: number): Promise<ActivityWindow[]> {
    return this._m.createStackedWindows(this.activity, windowTypes, timeout);
  }

  /**
   * The current activity context. To update use my.updateContext, to replace my.setContext
   *
   * @var {object} context
   */
  public get context(): object {
    const activity = this.activity;
    if (util.isUndefined(activity)) {
      return {};
    }

    return activity.context;
  }

  /**
   * Updates activity context using the properties from the context argument.
   * If old context is {a:1, b:2, c:3} and invoking updateContext({b:3, c:null}) will result a context
   * be {a:1, b:3}
   *
   * @function updateContext
   * @param {object} context
   * @return {Promise<object>}
   */
  public updateContext(context: object, callback?: any): Promise<object> {
    const activity = this.activity;
    if (util.isUndefined(activity)) {
      return new Promise<object>((resolve, reject) => {
        reject("Not in activity");
      });
    }

    return activity.updateContext(context, callback);
  }

  /**
   * Replaces the activity context with a new one.
   *
   * @function setContext
   * @param {object} context   The new context
   * @returns {Promise<Object>}
   */
  public setContext(context: object, callback?: any): Promise<object> {
    const activity = this.activity;
    if (util.isUndefined(activity)) {
      return new Promise((resolve, reject) => {
        reject("Not in activity");
      });
    }

    return activity.setContext(context, callback);
  }

  public onActivityJoined(callback: (activity: Activity) => void) {
    this._myActivityJoinedCallbacks.push(callback);

    // replay joined event
    const myWin = this.window;
    if (!util.isUndefinedOrNull(myWin) && !util.isUndefinedOrNull(myWin.activity)) {
      callback(myWin.activity);
    }
  }

  public onActivityLeft(callback: (activity: Activity) => void) {
    this._myActivityRemovedCallbacks.push(callback);
  }

  /**
   * Subscribe for context updates
   *
   * @function onContextChanged
   * @param {contextChangedHandler} callback Handler function that will receive context related events
   */
  public onContextChanged(callback: (context: object, delta: object, removed: string[], activity: Activity) => void) {
    this._myContextUpdateCallbacks.push(callback);

    // publish initial event
    const myWin = this.window;
    if (util.isUndefinedOrNull(myWin)) {
      return;
    }

    const activity = myWin.activity;
    if (util.isUndefinedOrNull(activity)) {
      return;
    }

    // NB: either the callback is added before the activity is recorded in the API, or
    // the activity is recorded before the callback
    // in the former case, when the activity is seen, the callback will be invoked in _notifyMyContextChanged
    // in the latter, the callback will be invoked here
    // in both cases, the callback will be invoked at least once as soon as an activity is recorded

    // see "long analysis for callback behavior in GW3: several cases" for full explanation

    callback(activity.context, activity.context, [], activity);
  }

  public clone(options: CloneOptions, callback: Callback<Activity>): Promise<Activity> {
    const act = this.activity;
    return this._m.clone(act, options, callback);
  }

  public attach(activity: Activity | string, tag?: object): Promise<AttachedActivityDescriptor> {
    let activityId: string;
    if (typeof activity === "string") {
      activityId = activity;
    } else {
      activityId = activity.id;
    }
    return this._m.attachActivities(activityId, this.activity.id, tag);
  }

  public onActivityAttached(callback: (state: AttachedActivityDescriptor) => void) {
    this._myAttached.push(callback);
  }

  public onActivityDetached(callback: (state: AttachedActivityDescriptor) => void) {
    this._myDetached.push(callback);
  }

  public onAttachedToActivity(callback: (activity: Activity, state: AttachedActivityDescriptor) => void) {
    this._myAttachedTo.push(callback);
  }

  public onDetachedFromActivity(callback: (detached: Activity, existing: Activity, state: AttachedActivityDescriptor) => void) {
    this._myDetachedFrom.push(callback);
  }

  public get attached() {
    if (!this.activity) {
      return [];
    }

    return this.activity.attached;
  }

  public setFrameColor(color: string, callback: () => void): Promise<Activity> {

    if (this.activity) {
      return this.activity.setFrameColor(color, callback);
    } else {
      return Promise.resolve(null);
    }
  }

  public getFrameColor(): string {

    if (this.activity) {
      return this.activity.getFrameColor();
    }

    return "";
  }

  public onFrameColorChanged(callback: () => void) {
    this._myActivityFrameColorChanged.push(callback);
  }

  private _subscribeMyContextChanged(activity: Activity, context: object, delta: object, removed: string[]) {
    const myWin = this.window;
    if (util.isUndefinedOrNull(myWin)) {
      return;
    }

    const myActivity = myWin.activity;
    if (util.isUndefinedOrNull(myActivity)) {
      return;
    }

    // ignore events not related to my activity
    if (activity.id !== myActivity.id) {
      return;
    }

    this._notifyMyContextChanged(activity, context, delta, removed);
  }

  private _subscribeMyWindowEvent(activity: Activity, window: ActivityWindow, event: any) {

    if (util.isUndefinedOrNull(this.window)) {
      return;
    }

    // ignore events not related to my window
    if (this.window.id !== window.id) {
      return;
    }

    if (event === EntityEventType.ActivityWindowJoinedActivity) {
      this._notifyMyWindowEvent(activity, this._myActivityJoinedCallbacks);

      // NB: tick42-contexts doesn't raise automatically an update event on join if the snapshot matches
      // the currently recorded data
      // revisited: however, it is pushing an update on subscribe() (which is performed by js-activity)
      // long analysis for callback behavior in GW3: several cases
      // 1 client calls onContextChanged before joined-activity arrives
      // then callback is pushed but onContextChanged callback call is skipped
      //    1-1 js-activity handles joined-activity first; it calls js-context subscribe
      //    1-1-1 js-context has not seen the activity so far so it doesn't invoke an update
      //    however, js-context then handles joined-activity and raises an update
      //    1-1-2 js-context has seen the activity, so it raises an update
      //    js-context then handles joined-activity and doesn't raise a second update unless the received snapshot is
      //    different from the last seen context value (can this even happen?)
      //    1-2 js-context handles joined-activity first
      //    since js-activity is not subscribed, whether it raises an update or not, it's not propagated
      //    js-activity then handles joined-activity, performs a subscribe and js-context raises an update
      // 2 joined-activity arrives, whatever updates are raised by js-activity's subscribe() or js-context's
      // message handler are not propagated since the callback is not pushed, client then calls onContextChanged,
      // and the callback is called inside that
      // also, the callback cannot be invoked first in 1 then 2 or first 2 then 1
      // => in all cases, a callback is called exactly once as soon as (it's subscribed && the activity is joined)
      // (in 1-1-2 it *might* be possible to be called twice, first with a slightly out of date context,
      // then with the joined-activity snapshot, see "different from the last seen context value", but it's unlikely,
      // and not a real issue anyway)
      // in the HC case, however, case 1 doesn't work since js-context doesn't raise updates on subscribe() nor
      // joining the activity (#deleteme TODO LATER MINOR: verify), so we need to raise it ourselves

      // 2017-09-08: final revisit (@vnikolov)
      // since SBGW_D-163 allows automatic subscription to contexts of pre-existing activities, 1-1-2 no longer works
      // (we might have already seen the latest state before joined-activity so no update will be raised)
      // for this reason I'm invoking notifyMyContextChanged unconditionally - so we might have multiple
      // invocations; if this turns into a problem we might add a guard inside notifyMyContextChanged that
      // checks we're not raising it twice with the same context value
      //
      // we might also get a duplicate raise if the sender of an update is also subscribed to the context
      // if (this._m.usingHc) {
      this._notifyMyContextChanged(activity, activity.context, null, null);
      // }

      // #deleteme TODO LATER MAJOR: what about the cases when we're not watching foreign activity contexts?
    } else if (event === EntityEventType.ActivityWindowLeftActivity) {
      this._notifyMyWindowEvent(activity, this._myActivityRemovedCallbacks);
      // #deleteme TODO LATER MINOR: should we send null update here?
    }
  }

  private _notifyMyWindowEvent(activity: Activity, callbackStore: ((activity: Activity, event: any) => void)[]) {
    callbackStore.forEach((element) => {
      try {
        element(activity, event);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _notifyMyContextChanged(activity: Activity, context: object, delta?: object, removed?: string[]) {
    delta = delta || {};
    removed = removed || [];

    this._myContextUpdateCallbacks.forEach((element) => {
      try {
        element(context, delta, removed, activity);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _notifyAttached(state: AttachedActivityDescriptor) {

    this._myAttached.forEach((cb) => {
      try {
        cb(state);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _notifyDetached(state: AttachedActivityDescriptor) {

    this._myDetached.forEach((cb) => {
      try {
        cb(state);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _notifyAttachedTo(state: AttachedActivityDescriptor) {

    this._myAttachedTo.forEach((cb) => {
      try {
        cb(this.activity, state);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _notifyDetachedFrom(detached: Activity, existing: Activity, state: AttachedActivityDescriptor) {

    this._myDetachedFrom.forEach((cb) => {
      try {
        cb(detached, existing, state);
      } catch (e) {
        this._logger.warn("error in user callback " + e);
      }
    });
  }

  private _subscribeActivitiesAttached(newAct: Activity, state: AttachedActivityDescriptor) {
    const myWin = this.window;
    if (util.isUndefinedOrNull(myWin)) {
      return;
    }

    const myActivity = myWin.activity;
    if (util.isUndefinedOrNull(myActivity)) {
      return;
    }

    // ignore events not related to my activity
    if (newAct.id !== myActivity.id) {
      return;
    }

    if (state.windowIds.indexOf(myWin.id) >= 0) {
      this._notifyAttachedTo(state);
      return;
    }

    this._notifyAttached(state);
  }

  private _subscribeActivitiesDetached(newAct: Activity, oldAct: Activity, state: any) {
    const myWin = this.window;
    if (util.isUndefinedOrNull(myWin)) {
      return;
    }

    const myActivity = myWin.activity;
    if (util.isUndefinedOrNull(myActivity)) {
      return;
    }

    // ignore events not related to my activity
    if (oldAct.id === myActivity.id) {
      this._notifyDetached(state);
    }

    if (newAct.id === myActivity.id) {
      this._notifyDetachedFrom(newAct, oldAct, state);
    }
  }

  private _subscribeWindowFrameColorChanged(window: Glue42.Windows.GDWindow) {
    const act = this.activity;
    if (!act) {
      return;
    }

    if (!act.owner) {
      return;
    }

    if (act.owner.underlyingWindow.id === window.id) {
      this._myActivityFrameColorChanged.forEach((callback) => {
        callback(window.frameColor);
      });
    }
  }
}
